<script>
import { onMount } from 'svelte';
import { get } from 'svelte/store';
import { _, locale } from 'svelte-intl';

import FormContainer from './FormContainer.svelte';
import Confirmation from './Confirmation.svelte';
import Input from './Input.svelte';
import Checkbox from './Checkbox.svelte';
import Button from './Button.svelte';

import firebase from 'firebase/app';
import { Firestore } from '../config/firebase';

// incoming values
export let date;

// state
let loading = true;
let dateExist = false;
let haveAvailableSeats = false;
let showSuccessMessage = false;
let times = [];
let currentLocale = get(locale);

let timeslots = {};

onMount(() => {
  if (date) {
    // requesting information
    Firestore
      .collection('marketdays')
      .doc(date)
      .onSnapshot(doc => {
        // subscribe to the firestore changes
        if (doc.exists) {
          timeslots = doc.data();
          updateTimes(timeslots);
        } else {
          dateExist = false;
          loading = false;
        }
      });
  } else {
    loading = false;
    dateExist = false;
  }
})

function updateTimes(data) {
  let totalAvailable = 0;

  // prepare the array
  times = Object.keys(data)
    .map(time => {
      let { label, seats: available, bookings } = data[time];
      if (bookings) {
        Object.keys(bookings).forEach(user => {
          available -= bookings[user]
        });
      }

      // check the total available seats
      totalAvailable += available;

      return {
        time,
        origLabel: label,
        label,
        available,
        value: time,
        disabled: available === 0
      }
    });
  times.sort((a, b) => a.time < b.time ? -1 : 1);

  // set error message
  haveAvailableSeats = (totalAvailable > 0);
  dateExist = true;
  loading = false;
}


// persons and timeslot dependancy logic
$: times = times.map(time => {
  const disabled = time.available < persons || 0;
  if (disabled && time.time === timeslot) {
    timeslot = '';
  }
  return Object.assign({}, time, { disabled });
});

// labels update on locale switch
$: times = times.map(time => {
  const label = (time.origLabel || time.time) + ' ' + $_('form.fields.timeslotSeats', { count: time.available });
  return Object.assign({}, time, { label });
});


// form values
let firstName;
let lastName;
let company;
let email;
let timeslot;
let persons = '';
let newsletter;
let gdpr;

let booking = 'new';

function submitForm(evt) {
  // if we're here, then the validation was passed ok

  // stop the browser to submit the form
  if (evt.preventDefault) {
    evt.preventDefault();
  }

  // Check if the user already have same booking to show different message
  if (
    timeslots[timeslot] &&
    timeslots[timeslot].bookings &&
    timeslots[timeslot].bookings[email]
  ) {
    booking = timeslots[timeslot].bookings[email] === persons
      ? 'same'
      : 'persons-updated';
  }

  // update the available seats for the date
  const bookingField = new firebase.firestore.FieldPath(timeslot, 'bookings', email);
  Firestore
    .collection('marketdays')
    .doc(date)
    .update(bookingField, persons)
    .then(() => {
      showSuccessMessage = true;
      firebase.analytics().logEvent('booking', { date: date, booking: timeslot });
    });
  
  // update the user data
  const bookingTimeslotField = new firebase.firestore.FieldPath('bookings', `${date}T${timeslot}`);
  Firestore
    .collection('marketdays-users')
    .doc(email)
    .set({
      firstName,
      lastName,
      company,
      email,
      newsletter,
      gdpr,
      lastDate: date,
      lastTime: timeslot,
      lastPersons: persons,
      lang: currentLocale
    }, {
      merge: true
    }).then(() => {
      Firestore
        .collection('marketdays-users')
        .doc(email)
        .update(bookingTimeslotField, persons);
    });
}
</script>

{#if loading}
  <div class="loading">{$_('loading')}</div>
{:else if showSuccessMessage}
  <Confirmation
    type="success"
    message={$_(`success.${booking}.message`)}
    description={$_(`success.${booking}.messageHint`)}
  />
{:else if !dateExist}
  <Confirmation
    type="warning"
    message={$_('noEvents.message')}
    description={$_('noEvents.messageHint')}
  />
{:else if !haveAvailableSeats}
  <Confirmation
    type="warning"
    message={$_('noSeats.message')}
    description={$_('noSeats.messageHint')}
  />
{:else}
  <FormContainer date={date}>
    <form action="" on:submit={submitForm}>

      <Input name="firstName" bind:value={firstName} label={$_('form.fields.firstName')} mandatory />
      <Input name="lastName" bind:value={lastName} label={$_('form.fields.lastName')} mandatory />
      <Input name="email" bind:value={email} label={$_('form.fields.emailAddress')} mandatory type="email" />
      <Input name="company" bind:value={company} label={$_('form.fields.company')} mandatory />
      <Input name="persons" bind:value={persons} label={$_('form.fields.persons')} mandatory type="select"
        items={[
          { label: $_('form.fields.personsSelect'), value: '' },
          { label: $_('form.fields.personsNumber', { count: 1 }), value: 1 },
          { label: $_('form.fields.personsNumber', { count: 2 }), value: 2 },
          { label: $_('form.fields.personsNumber', { count: 3 }), value: 3 },
          { label: $_('form.fields.personsNumber', { count: 4 }), value: 4 },
        ]}
      />
      {#if persons !== ''}
        <Input name="timeslot" bind:value={timeslot} label={$_('form.fields.timeslot')} mandatory type="select" items={[
          { label: $_('form.fields.timeslotSelect'), value: '' },
          ...times
        ]} />
      {/if}

      <Checkbox name="gdpr" bind:value={gdpr} label={$_('form.fields.gdpr')} mandatory />
      <Checkbox name="newsletter" bind:value={newsletter} label={$_('form.fields.newsletter')}/>

      <Button label={$_('form.bookButton')} legend={$_('form.buttonHint')}/>
    </form>
  </FormContainer>
{/if}


<style>
.loading {
  text-align: center;
  padding: 66px 0;
}

form {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}
</style>
